import { onScopeDispose } from "vue";

/**
 * Call `tick` every `intervalMs` while the page is showing, and never while it is hidden. A
 * parent's phone in a pocket with the tab still open would otherwise keep reading every few
 * seconds, at the busiest moment of an event, for a page nobody is looking at. When the page shows
 * again, `tick` runs at once and the interval starts over, so what they see is current.
 *
 * Nothing runs until `start()`, so a page can do its first read itself. `stop()` ends it (until the
 * next `start()`); leaving the page ends it for good, and a `start()` after that is ignored, so a
 * first read that outlasts the page can't leave a poll behind. `tick` handles its own errors.
 */
export function useVisiblePoll(tick: () => unknown, intervalMs: number) {
  let timer: ReturnType<typeof setInterval> | undefined;
  let running = false;
  let disposed = false;

  const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

  const clearTimer = () => {
    clearInterval(timer);
    timer = undefined;
  };

  const arm = () => {
    clearTimer();
    if (running && !hidden()) timer = setInterval(() => void tick(), intervalMs);
  };

  const onVisibilityChange = () => {
    if (!running) return;
    if (hidden()) {
      clearTimer();
      return;
    }
    void tick();
    arm();
  };

  const start = () => {
    if (disposed || running) return;
    running = true;
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    arm();
  };

  const stop = () => {
    running = false;
    clearTimer();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  };

  onScopeDispose(() => {
    disposed = true;
    stop();
  });

  return { start, stop };
}
